import { displayApiMessage, normalizeApiError } from '../api/apiError';

export class FaceFlowError extends Error {
  constructor(public readonly code: string, message?: string) {
    super(message ?? faceAttendanceErrorMessage(code));
    this.name = 'FaceFlowError';
  }
}

const CLIENT_MESSAGES: Record<string, string> = {
  AZURE_LIVENESS_NATIVE_MODULE_UNAVAILABLE: 'Liveness is unavailable on this device.',
  AWS_LIVENESS_UI_UNAVAILABLE:
    'AWS Amplify Face Liveness UI is not installed on this build.',
  AWS_LIVENESS_REGION_REQUIRED: 'AWS liveness region is not configured.',
  AWS_COGNITO_IDENTITY_POOL_REQUIRED:
    'AWS Cognito Identity Pool is not configured for Face Liveness.',
  LIVENESS_CANCELED: 'Liveness verification was canceled.',
  LIVENESS_NOT_COMPLETED: 'Complete the liveness check before continuing.',
  LIVENESS_SESSION_EXPIRED: 'The liveness session expired. Start again.',
  LIVENESS_RESULT_TIMEOUT: 'Liveness verification took too long. Start again.',
  LIVENESS_FAILED: 'Liveness verification did not pass.',
  CAPTURE_CANCELED: 'Camera capture was canceled.',
  CAPTURE_FAILED: 'Camera capture failed. Try again.',
};

export function faceAttendanceErrorMessage(code: string) {
  return CLIENT_MESSAGES[code] ?? 'Face attendance is temporarily unavailable.';
}

export function safeFaceFlowError(error: unknown): FaceFlowError {
  if (error instanceof FaceFlowError) return error;

  const axiosData =
    typeof error === 'object' &&
    error &&
    'response' in error
      ? (error as { response?: { data?: { code?: unknown; message?: unknown } } })
          .response?.data
      : undefined;
  const axiosCode =
    typeof axiosData?.code === 'string' ? axiosData.code : null;
  const axiosMessage =
    typeof axiosData?.message === 'string'
      ? displayApiMessage(axiosData.message)
      : null;

  const apiError = normalizeApiError(error);
  const raw = axiosMessage || apiError.message;
  const code =
    axiosCode ||
    apiError.code ||
    (apiError.code === 'NETWORK_ERROR' || apiError.code === 'REQUEST_TIMEOUT'
      ? apiError.code
      : null) ||
    'FACE_PUNCH_FAILED';

  return new FaceFlowError(code, raw || faceAttendanceErrorMessage(code));
}
